import { CustomerOrderBase } from '../../contracts/customer-order.js';
import { ArgumentException } from '../../exceptions.js';
import { Currency } from '../../models/serializable/currency.js';
import { OnlineProductOrder, OnlineProductOrderStatus } from '../../models/serializable/online-product-order.js';
import { Price } from '../../models/serializable/price.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { FeeHelper } from '../fee-helper.js';
import { LegacyOrderDiscountBreakdown } from './discount-breakdown.js';
import { LegacyOrderFeeBreakdown, LegacyOrderFeeBreakdownEntry } from './fee-breakdown.js';
import { BasicOrderPriceBreakdown, IOrderPriceBreakdown } from './order-price-breakdown.js';
import { IPaymentHelperAdapter } from './payment-helper-adapter.js';
import { IPaymentHelperConfig, isProductOrderRefundConfig } from './payment-helper-config.js';

export class ProductOrderPaymentHelper extends IPaymentHelperAdapter {
  public readonly orderTypeId: string = OnlineProductOrder.TYPE_ID;

  static verifyType(dm: IPaymentHelperAdapter): boolean {
    return dm instanceof ProductOrderPaymentHelper;
  }

  static assertType(dm: IPaymentHelperAdapter): dm is ProductOrderPaymentHelper {
    return dm instanceof ProductOrderPaymentHelper;
  }

  getCurrencyOfOrder(order: CustomerOrderBase): Currency | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return order.items.length > 0 ? order.items[0].product.price.currency : null;
  }

  calculateDiscountForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateCheckoutPriceBreakdownForOnlineOrder(order: CustomerOrderBase): IOrderPriceBreakdown | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    const itemPrice = this.calculatePriceForOnlineProductOrder({ order, applyDonation: false, applyFee: false });
    if(itemPrice == null) {
      return null;
    }

    const donationPrice = order.donation?.clone() ?? null;
    const standardFeePrice = order.applicationFee?.clone() ?? null;
    const totalFeePrice = order.applicationFee?.clone() ?? null;

    let totalPrice = itemPrice.clone();
    if(donationPrice != null) {
      totalPrice = totalPrice.add(donationPrice);
    }
    if(totalFeePrice != null) {
      totalPrice = totalPrice.add(totalFeePrice);
    }

    return BasicOrderPriceBreakdown.product({ itemPrice, donationPrice, standardFeePrice, totalFeePrice, totalPrice });
  }

  calculateDiscountBreakdownForOrder(order: CustomerOrderBase): LegacyOrderDiscountBreakdown | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return null;
  }

  calculateFeeBreakdownForOrder(order: CustomerOrderBase): LegacyOrderFeeBreakdown | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    const entries: LegacyOrderFeeBreakdownEntry[] = [];

    for(let pi = 0; pi < order.items.length; pi++) {
      const orderItem = order.items[pi];
      const result = FeeHelper.calculateFeeValueForProductOrderItem(orderItem, { applyScaling: false });

      if(result != null) {
        entries.push({ ...result, host: { type: 'product', index: pi } });
      }
    }

    return { entries };
  }

  calculateCheckoutPriceForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineProductOrder({ order, applyDonation: true, applyFee: true });
  }

  calculateCheckoutPriceForDiscountProvider(order: CustomerOrderBase): Price | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineProductOrder({ order, applyDonation: false, applyFee: false });
  }

  calculateIncomeForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineProductOrder({ order, applyDonation: false, applyFee: false });
  }

  calculateIncomeWithoutDiscountForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculatePriceForOnlineProductOrder({ order, applyDonation: false, applyFee: false });
  }

  canCreateRefundForOnlineOrder(order: CustomerOrderBase): boolean | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.canCreateRefundForOnlineProductOrder(order);
  }

  calculateExistingRefundForOrder(order: CustomerOrderBase): Price | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }

    return this.calculateExistingRefundForOnlineProductOrder(order);
  }

  calculateRemainingRefundForOrder(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }
    if(!isProductOrderRefundConfig(config)) {
      throw new ArgumentException('config', 'Config type is not valid');
    }

    return this.calculateRemainingRefundForOnlineProductOrder(order, config);
  }

  calculateMaxRefundForOnlineOrder(order: CustomerOrderBase, config: IPaymentHelperConfig): Price | null {
    if(!OnlineProductOrder.assertType(order)) {
      throw new ArgumentException('order', 'Order type is not valid');
    }
    if(!isProductOrderRefundConfig(config)) {
      throw new ArgumentException('config', 'Config type is not valid');
    }

    return this.calculateMaxRefundForOnlineProductOrder(order, config);
  }

  private calculatePriceForOnlineProductOrder({ order, applyDonation, applyFee }: {
    order: OnlineProductOrder,
    applyDonation: boolean,
    applyFee: boolean,
  }): Price | null {
    const currency = this.getCurrencyOfOrder(order);
    if(isNullOrUndefined(currency)) {
      return null;
    }

    let orderPrice = Price.zero(currency);

    for(const item of order.items) {
      if(item.product.price.currency != currency) {
        return null;
      }

      orderPrice = orderPrice.add(item.product.price.scale(item.quantity));
    }

    if(applyFee && isNotNullOrUndefined(order.applicationFee)) {
      orderPrice = orderPrice.add(order.applicationFee);
    }

    if(applyDonation && isNotNullOrUndefined(order.donation)) {
      orderPrice = orderPrice.add(order.donation);
    }

    return orderPrice;
  }

  private canCreateRefundForOnlineProductOrder(order: OnlineProductOrder): boolean {
    if(order.status !== OnlineProductOrderStatus.Refunded) {
      return false;
    }

    return order.refunds.length <= 0;
  }

  private calculateExistingRefundForOnlineProductOrder(order: OnlineProductOrder): Price | null {
    let currency: Currency | null = null;
    if(order.items.length > 0) {
      currency = order.items[0].product.price.currency;
    }

    if(isNullOrUndefined(currency)) {
      return null;
    }

    let totalRefundSoFar = Price.zero(currency);
    for(const refund of order.refunds) {
      totalRefundSoFar = totalRefundSoFar.add(refund.price);
    }

    return totalRefundSoFar;
  }

  private calculateRemainingRefundForOnlineProductOrder(
      order: OnlineProductOrder,
      options: {
        includeStandardFee: boolean,
      }
  ): Price | null {
    let orderPrice = this.calculateIncomeForOrder(order);
    if(isNullOrUndefined(orderPrice)) {
      return null;
    }

    if(order.applicationFee != null && options.includeStandardFee) {
      orderPrice = orderPrice.add(order.applicationFee);
    }

    let totalRefundSoFar = Price.zero(orderPrice.currency);
    for(const refund of order.refunds) {
      totalRefundSoFar = totalRefundSoFar.add(refund.price);
    }

    return orderPrice.difference(totalRefundSoFar);
  }

  private calculateMaxRefundForOnlineProductOrder(
      order: OnlineProductOrder,
      options: {
        includeStandardFee: boolean,
      }
  ): Price | null {
    let orderPrice = this.calculateIncomeForOrder(order);
    if(isNullOrUndefined(orderPrice)) {
      return null;
    }

    if(order.applicationFee != null && options.includeStandardFee) {
      orderPrice = orderPrice.add(order.applicationFee);
    }

    return orderPrice;
  }
}
